import { Component, createRef } from "react";
import { EnvService, FilterItem, FilterItemOperator, IStorageLocal, NamingConvention } from "namirasoft-core";
import { BaseColumnFormatter, DateTimeFormatter, IDFormatter, NSLabel, NSLoading, NSTable, NSTabPage, StringFormatter, NSTabPageTab, StringFormatterSizeType, NSNoData } from "namirasoft-site-react";
import { HistoryRow, NamirasoftHistoryServer } from "namirasoft-history";
import { IEntityInfo } from "../IEntityInfo";
import { NSARouterMakerProps } from "../NSARouterMakerProps";
import { CategoryRow, FieldRow } from "namirasoft-field";
import { CFTUtil } from "./CFTUtil";
import { NSASectionViewTabMore } from "./NSASectionViewTabMore";
import { NamirasoftMap } from "namirasoft-site-map";
import Styles from "./NSASectionViewTabPage.module.css";

export interface NSASectionViewTabPageUIProps
{
	enabled?: boolean;
	tabs?: {
		general?: boolean;
		field?: boolean;
		category?: boolean;
		tag?: boolean;
		history?: boolean;
	};
}

export interface NSASectionViewTabPageCutomProps<RowType>
{
	ui?: NSASectionViewTabPageUIProps;
	onRowLoaded?: (row: RowType) => void;
}

export interface NSASectionViewTabPageProps<RowType extends { id: string }, RowTypeInput = RowType> extends NSARouterMakerProps, NSASectionViewTabPageCutomProps<RowType>
{
	entity: IEntityInfo<RowType, RowTypeInput>;
	ctf: {
		enabled: boolean;
		fields: FieldRow[] | null;
		categories: CategoryRow[] | null;
	};
	model: {
		id: string | null;
		row: RowType | null;
	};
	deleteFieldCache: () => void;
}

interface NSASectionViewTabPageState<RowType>
{
	row: RowType | null;
}

export class NSASectionViewTabPage<RowType extends { id: string }, RowTypeInput = RowType> extends Component<NSASectionViewTabPageProps<RowType, RowTypeInput>, NSASectionViewTabPageState<RowType>>
{
	NSTabPage_Ref = createRef<NSTabPage>();

	constructor(props: NSASectionViewTabPageProps<RowType, RowTypeInput>)
	{
		super(props);

		this.state = { row: this.props.model.row };

		this.reload = this.reload.bind(this);
		this.getRow = this.getRow.bind(this);
	}
	reload() 
	{
		this.setState(prev => ({ ...prev, row: null }));
		if (this.props.model.id)
			if (this.props.entity.server.get)
				this.props.entity.server.get(this.props.model.id).then(row =>
				{
					this.setState(prev => ({ ...prev, row }), () =>
					{
						this.props.onRowLoaded?.(row);
					});
				}).catch(() => { });
	}
	getRow()
	{
		return this.state.row;
	}
	override componentDidMount()
	{
		if (this.props.model.id)
			if (!this.props.model.row || this.props.model.row.id !== this.props.model.id)
				this.reload();
	}
	override render()
	{
		const tabs: NSTabPageTab[] = [];

		if (this.props.model.id)
		{
			if (this.props.entity.server.get)
				if (this.props.ui?.tabs?.general ?? true)
					tabs.push({
						title: 'General',
						clicked: false,
						getContent: () =>
						{
							if (!this.state.row)
								return <NSLoading></NSLoading>;
							return <div className={Styles.nsa_section_tab_page_parent}>
								{
									this.props.entity.client.getColumns().map((column, index) =>
									{
										let value = (this.state.row as any)[column.name];
										let formatted = column.formatter.format(value, column, { index: 0, value: this.state.row }, false);
										return <NSLabel
											key={`NSASectionViewTabPage_NSLabel_General_${index}`}
											title={column.text + ":"}
											classList={[Styles.nsa_section_tab_page_item]}
										>
											<div>{formatted}</div>
										</NSLabel>
									})
								}
							</div>;
						}
					});

			if (this.props.entity.server.get)
				if (this.props.ctf.enabled)
				{
					if (this.props.ui?.tabs?.field ?? true)
					{
						let groups = Array.from(new Set(this.props.ctf.fields?.map(x => x.group) ?? []));
						groups.forEach(group =>
						{
							tabs.push({
								title: group,
								clicked: false,
								getContent: () =>
								{
									if (this.state.row == null)
										return <NSLoading></NSLoading>;
									let ctf = CFTUtil.get(this.props.entity.name, this.state.row);
									if (ctf.fields.length === 0)
										return <NSNoData
											lable="No field found" />
									return <div className={Styles.nsa_section_tab_page_parent}>
										{
											this.props.ctf.fields?.filter(x => x.group === group).map((field, index) =>
											{
												return <NSLabel
													key={`NSASectionViewTabPage_NSLabel_Field_${index}`}
													title={field.name + ":"}
													classList={[Styles.nsa_section_tab_page_item]}
												>
													<div>{ctf.fields.filter(f => f.field_id === field.id).map(f => f.value).join(", ")}</div>
												</NSLabel>
											})
										}
									</div>;
								}
							});
						});
					}
					if (this.props.ui?.tabs?.category ?? true)
					{
						let groups = Array.from(new Set(this.props.ctf.categories?.map(x => x.group) ?? []));
						groups.forEach(group =>
						{
							tabs.push({
								title: group,
								clicked: false,
								getContent: () =>
								{
									if (this.state.row == null)
										return <NSLoading></NSLoading>;
									let ctf = CFTUtil.get(this.props.entity.name, this.state.row);
									if (ctf.tags.length === 0)
										return <NSNoData
											lable="No category found" />
									let ids = ctf.categories.map(c => c.category_id);
									return <div className={Styles.nsa_section_tab_page_parent}>
										{
											this.props.ctf.categories?.filter(x => x.group === group && ids.includes(x.id)).map((category, index) =>
											{
												return <NSLabel
													key={`NSASectionViewTabPage_NSLabel_Category_${index}`}
													title={category.name}
													classList={[Styles.nsa_section_tab_page_item]}
												>
												</NSLabel>
											})
										}
									</div>;
								}
							});
						});
					}
					if (this.props.ui?.tabs?.tag ?? true)
						tabs.push({
							title: 'Tag',
							clicked: false,
							getContent: () =>
							{
								if (this.state.row == null)
									return <NSLoading></NSLoading>;
								let ctf = CFTUtil.get(this.props.entity.name, this.state.row);
								if (ctf.tags.length === 0)
									return <NSNoData lable="No tag found" />
								return <div className={Styles.nsa_section_tab_page_parent}>
									{
										ctf.tags?.map((tag, index) =>
										{
											return <NSLabel
												key={`NSASectionViewTabPage_NSLabel_Tag_${index}`}
												title={tag.name + ":"}
												classList={[Styles.nsa_section_tab_page_item]}
											>
												<div>{tag.value}</div>
											</NSLabel>
										})
									}
								</div>;
							}
						});
				}
			//todo
			// tabs.push({title: 'Price History', getContent: todo });
			// tabs.push({title: 'Price Estimator', getContent: todo });
			// tabs.push({title: 'Log', getContent: todo });
			// tabs.push({title: 'Monitor', getContent: todo });
			let namirasoft = new NamirasoftMap(this.props.account.token_manager, this.props.notifier.onError);
			if (this.props.ui?.tabs?.history ?? true)
				tabs.push({
					title: 'History',
					clicked: false,
					getContent: () =>
					{
						return <NSTable<HistoryRow>
							name="History"
							checkbox={false}
							columns={[
								BaseColumnFormatter.getColumn(namirasoft.history.tables.history.columns.id, new IDFormatter(namirasoft, null)),
								BaseColumnFormatter.getColumn(namirasoft.history.tables.history.columns.user_id, new IDFormatter(namirasoft, new IStorageLocal())),
								BaseColumnFormatter.getColumn(namirasoft.history.tables.history.columns.session_id, new IDFormatter(namirasoft, null)),
								BaseColumnFormatter.getColumn(namirasoft.history.tables.history.columns.entity_name, new StringFormatter(StringFormatterSizeType.TwoWords, NamingConvention.Pascal_Case_Space)),
								BaseColumnFormatter.getColumn(namirasoft.history.tables.history.columns.entity_id, new IDFormatter(namirasoft, new IStorageLocal())),
								BaseColumnFormatter.getColumn(namirasoft.history.tables.history.columns.action, new StringFormatter(StringFormatterSizeType.Word)),
								BaseColumnFormatter.getColumn(namirasoft.history.tables.history.columns.created_at, new DateTimeFormatter()),
							]}
							getRows={async () =>
							{
								if (this.props.model.id)
								{
									let REACT_APP_BASE_URL_HISTORY = new EnvService("REACT_APP_BASE_URL_HISTORY", true).getString();
									let server = new NamirasoftHistoryServer(REACT_APP_BASE_URL_HISTORY, this.props.account.token_manager, this.props.notifier.onError);
									let filters = [new FilterItem(namirasoft.history.tables.history, namirasoft.history.tables.history.columns.entity_id, false, FilterItemOperator.all.equals, this.props.model.id)]
									return await server.history.List(filters, null, null, []);
								}
								return { rows: [], count: 0 };
							}}
							getRowKey={row => row.value.id}
							style={{ width: "100%" }}
						></NSTable>;
					}
				});

			if (this.props.entity.server.get)
				if (this.props.ctf.enabled)
					tabs.push({
						title: "More",
						clicked: true,
						getContent: () => <NSASectionViewTabMore
							deleteFieldCache={this.props.deleteFieldCache}
						/>
					});
			//todo
			// tabs.push({title: 'How It Works', getContent: todo });
			// tabs.push({title: 'API', getContent: todo });
			// tabs.push({title: 'Sample Code', getContent: todo });
			//todo
			// tabs.push({title: 'Category', getContent: todo });
			// tabs.push({title: 'Field', getContent: todo });
		}

		if (!(this.props.ui?.enabled ?? true))
			return <></>;
		return (
			<>
				{
					tabs.length > 0 &&
					<NSTabPage ref={this.NSTabPage_Ref} tabs={tabs} />
				}
			</>
		);
	}
}